import React, { useContext, useState } from 'react';
import { IconButton, TextField, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import SendIcon from '@mui/icons-material/Send';
import { IntlShape } from 'react-intl';
import { assign } from 'xstate';
import { cloneDeep } from 'lodash';

import { SendContext } from '../../SendContext';
import { BaseComponent } from '../Component';
import { MachineContext } from '../../MachineContext';
import { translateWithFallback } from '../../util';

import styles from './FormattedInput.module.css';
import { FormattedInputEnum } from './FormattedInputEnum';

export interface FormattedInputComponent extends BaseComponent {
  type: 'formattedInput';
  props: FormattedInputProps;
}

export interface FormattedInputProps {
  placeholderText: string;
  helperText: string;
  saveResultTo: string;
  defaultValue?: string;
  formatType: FormattedInputEnum;
}

const useStyles = makeStyles(() => ({
  noBorder: {
    border: 'none',
  },
}));

export const validate = (
  input: string,
  formatType: FormattedInputEnum
): boolean => {
  const mailRegex = new RegExp(
    /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
  );
  const phoneRegex = new RegExp(
    /^[+]?[\s./0-9]*[(]?[0-9]{1,4}[)]?[-\s./0-9]*$/g
  );
  const urlRegex = new RegExp(
    /(http(s)?:\/\/.)?(www\.)?[-a-zA-Z0-9@:%._\+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_\+.~#?&//=]*)/g
  );

  switch (formatType) {
    case FormattedInputEnum.mail:
      return mailRegex.test(input);
    case FormattedInputEnum.phone:
      return phoneRegex.test(input);
    case FormattedInputEnum.url:
      return urlRegex.test(input);
    default:
      return false;
  }
};

export const FormattedInput: React.FC<FormattedInputProps> = ({
  placeholderText,
  helperText,
  saveResultTo,
  defaultValue,
  formatType,
}) => {
  const { send } = useContext(SendContext);
  const [message, setMessage] = useState<string>(defaultValue ?? '');
  const [isValidInput, setValidInput] = useState<boolean>(true);
  const classes = useStyles();

  return (
    <Typography component="span">
      <div className={styles['message-input-box']}>
        <TextField
          className={styles['message-input']}
          placeholder={placeholderText}
          value={message}
          error={isValidInput}
          helperText={isValidInput ? '' : helperText}
          fullWidth
          variant="outlined"
          InputProps={{
            classes: { notchedOutline: classes.noBorder },
          }}
          onChange={(event) => {
            const value = event.target.value;
            setMessage(value);
            setValidInput(true);
          }}
        ></TextField>
        <div className={styles['send-button']}>
          <IconButton
            onClick={() => {
              if (validate(message, formatType)) {
                send({
                  type: 'writeToContext',
                  payload: { [saveResultTo]: message },
                });
                send({
                  type: 'next',
                });
              } else {
                setValidInput(false);
              }
            }}
            size="large"
          >
            <SendIcon />
          </IconButton>
        </div>
      </div>
    </Typography>
  );
};

export const renderFormattedInputAction = (intl: IntlShape) => {
  return assign((context: MachineContext, _event, actionMetadata) => {
    const newContext = cloneDeep(context);
    newContext.widgetData = newContext.widgetData.filter(
      (element) => !element.temporary
    );
    const payload = actionMetadata.action.payload;

    const getValueFrom = payload.getValueFrom;
    let defaultValue = payload.defaultValue;

    if (getValueFrom !== undefined) {
      defaultValue = context.userData[getValueFrom];
    }

    newContext.widgetData.push({
      type: 'formattedInput',
      temporary: actionMetadata.action.temporary ?? false,
      props: {
        placeholderText: translateWithFallback(intl, payload.placeholderText),
        helperText: translateWithFallback(intl, payload.helperText),
        saveResultTo: payload.saveResultTo,
        formatType: payload.formatType,
        defaultValue: defaultValue,
      },
    });
    return newContext;
  });
};
